import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { UAParser } from 'ua-parser-js';

import { DataWrapper } from 'components';
import { DateFormat } from 'enums';
import { TranslationNamespace } from 'i18n';
import { OrderSciEvent } from 'types';
import { formatUtils } from 'utils';

const parseUserAgent = (ua: string): { os: string; browser: string } => {
  const result = new UAParser(ua).getResult();
  const os = [result.os.name, result.os.version].filter(Boolean).join(' ');
  const browser = [result.browser.name, result.browser.version]
    .filter(Boolean)
    .join(' ');
  return { os, browser };
};

type Props = {
  queryResult: UseQueryResult<OrderSciEvent[]>;
};

type SessionGroup = {
  sessionId: string;
  ip: string | null;
  userAgent: string | null;
  events: OrderSciEvent[];
};

const groupBySession = (items: OrderSciEvent[]): SessionGroup[] => {
  const groups: SessionGroup[] = [];
  const sessionIndexMap = new Map<string, number>();

  for (const item of items) {
    const sid = item.sessionId ?? '';
    if (sessionIndexMap.has(sid)) {
      groups[sessionIndexMap.get(sid)!].events.push(item);
    } else {
      sessionIndexMap.set(sid, groups.length);
      groups.push({
        sessionId: sid,
        ip: item.ip ?? null,
        userAgent: item.userAgent ?? null,
        events: [item],
      });
    }
  }
  return groups;
};

const MessageCell: React.FC<{ item: OrderSciEvent }> = ({ item }) => {
  const [stackVisible, setStackVisible] = useState(false);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.sci_events',
  });

  const toggleStack = useCallback(() => setStackVisible((v) => !v), []);

  if (!item.message && !item.stack && !item.action && item.duration == null) {
    return null;
  }

  return (
    <div>
      {item.message && <div>{item.message}</div>}
      {/** @deprecated Migrated to event. To be dropped after migration. */}
      {item.action && (
        <div className="tw-text-xs tw-text-secondary tw-mt-0.5">
          {item.action}
        </div>
      )}
      {/** @deprecated Migrated to event. To be dropped after migration. */}
      {item.duration && (
        <div className="tw-text-xs tw-text-secondary tw-mt-0.5">
          {item.duration}
        </div>
      )}
      {/** @deprecated Migrated to event. To be dropped after migration. */}
      {item.stack && (
        <button
          className="tw-text-xs tw-text-blue-500 tw-underline tw-mt-1"
          onClick={toggleStack}
          type="button"
        >
          {stackVisible ? t('hide_stack') : t('show_stack')}
        </button>
      )}
      {stackVisible && item.stack && (
        <pre className="tw-text-xs tw-mt-1 tw-whitespace-pre-wrap tw-break-all tw-bg-gray-100 tw-p-1 tw-rounded">
          {item.stack}
        </pre>
      )}
    </div>
  );
};

type SessionHeaderProps = {
  group: SessionGroup;
};

const SessionHeader: React.FC<SessionHeaderProps> = ({ group }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.sci_events',
  });
  const theme = useTheme();

  const firstEvent = group.events[0];
  const lastEvent = group.events[group.events.length - 1];
  const ua = group.userAgent ? parseUserAgent(group.userAgent) : null;

  return (
    <div
      className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-0.5 tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-t tw-border tw-border-b-0"
      style={{
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.divider,
      }}
    >
      {group.sessionId && (
        <span
          className="tw-font-mono tw-px-1.5 tw-py-0.5 tw-rounded tw-font-semibold tw-bg-black/[0.07]"
          title={group.sessionId}
        >
          {group.sessionId.slice(0, 8)}
        </span>
      )}
      <span className="tw-text-secondary">
        {formatUtils.formatDate(firstEvent.createdAt, DateFormat.Full)}
        {group.events.length > 1 && (
          <>
            {' '}
            {'→'} {formatUtils.formatDate(lastEvent.createdAt, DateFormat.Full)}
          </>
        )}
      </span>
      {group.ip && (
        <span className="tw-text-secondary">{`IP: ${group.ip}`}</span>
      )}
      {ua?.os && (
        <span className="tw-text-secondary">
          {t('client_os', { os: ua.os })}
        </span>
      )}
      {ua?.browser && (
        <span className="tw-text-secondary">
          {t('client_browser', { browser: ua.browser })}
        </span>
      )}
    </div>
  );
};

export const SciEventsTable: React.FC<Props> = ({ queryResult }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.sci_events',
  });
  const theme = useTheme();

  const groups = useMemo(
    () => groupBySession(queryResult.data ?? []),
    [queryResult.data],
  );

  const eventColumns = useMemo(
    () => (
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 170 }}>{t('columns.created_at')}</TableCell>
          <TableCell sx={{ width: 300 }}>{t('columns.event')}</TableCell>
          <TableCell className="tw-break-all">{t('columns.message')}</TableCell>
        </TableRow>
      </TableHead>
    ),
    [t],
  );

  const eventRows = (events: OrderSciEvent[]) =>
    events.map((item) => (
      <TableRow key={item.id}>
        <TableCell>
          {formatUtils.formatDate(item.createdAt, DateFormat.Full)}
        </TableCell>
        <TableCell>
          {t(`event_type.${item.event}`, { defaultValue: item.event })}
        </TableCell>
        <TableCell className="tw-break-all">
          <MessageCell item={item} />
        </TableCell>
      </TableRow>
    ));

  return (
    <DataWrapper queryResult={queryResult}>
      <div className="tw-flex tw-flex-col tw-gap-3">
        {groups.map((group) => (
          <div key={group.sessionId || group.events[0]?.id}>
            <SessionHeader group={group} />
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '0 0 4px 4px',
                border: `1px solid ${theme.palette.divider}`,
              }}
              elevation={0}
            >
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                {eventColumns}
                <TableBody>{eventRows(group.events)}</TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </div>
    </DataWrapper>
  );
};
